import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiUrls } from "../../utils/apiUrls";
import { API, callAPIWithoutAuth } from "../../utils/apiUtils";
import { queryClient } from "../../providers/QueryProvider";

export const loginUser = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await callAPIWithoutAuth(
        apiUrls.signin,
        {},
        "POST",
        credentials,
      );

      if (response?.data?.success) {
        const token = response?.data?.data?.token;
        const user = response?.data?.data?.user;

        localStorage.setItem("accessToken", token);
        localStorage.setItem("userData", JSON.stringify(user));

        return { token, user };
      } else {
        return rejectWithValue(response?.data?.message || "Login failed");
      }
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || "Login failed",
      );
    }
  },
);

export const sendOtp = createAsyncThunk(
  "auth/sendOtp",
  async (email, { rejectWithValue }) => {
    try {
      const response = await callAPIWithoutAuth(apiUrls.sendOtp, {}, "POST", {
        email,
      });

      if (response?.data?.success) {
        return response?.data?.data?.userId || response?.data?.data?._id;
      } else {
        return rejectWithValue(response?.data?.message || "Failed to send OTP");
      }
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send OTP",
      );
    }
  },
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ userId, otp, type = "VERIFY" }, { rejectWithValue }) => {
    try {
      const response = await callAPIWithoutAuth(apiUrls.verifyOtp, {}, "POST", {
        _id: userId,
        otp,
        type,
      });
      if (response?.data?.success) {
        return response?.data || response?.data?.message;
      } else {
        return response?.data?.message || "Invalid OTP";
      }
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || "Invalid OTP",
      );
    }
  },
);
export const verifySignup = createAsyncThunk(
  "auth/verifySignup",
  async ({ userId, otp, type = "VERIFY" }, { rejectWithValue }) => {
    try {
      const response = await callAPIWithoutAuth(
        apiUrls.verifySignup,
        {},
        "POST",
        {
          _id: userId,
          otp,
          type,
        },
      );
      if (response?.data?.success) {
        const token = response?.data?.data?.token;
        const user = response?.data?.data?.user;

        localStorage.setItem('accessToken', token);
        localStorage.setItem('userData', JSON.stringify(user));

        return {
          token,
          user,
          success: true,
          message: response?.data?.message || "OTP verified successfully",
        };
      } else {
        return rejectWithValue(response?.data?.message || "Invalid OTP");
      }
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || "Invalid OTP",
      );
    }
  },
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('walletAccountLinked');
    localStorage.removeItem('walletData');
    localStorage.removeItem('walletBalance');
    localStorage.removeItem('walletReturnPath');
    localStorage.removeItem('bankAccounts');
    queryClient.clear();
    return null;
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email, { rejectWithValue }) => {
    try {
      const response = await callAPIWithoutAuth(
        apiUrls.forgotPassword,
        {},
        "POST",
        { email },
      );

      if (response?.data?.success) {
        return response?.data?.data;
      } else {
        return rejectWithValue(response?.data?.message || "Failed to send OTP");
      }
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send OTP",
      );
    }
  },
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ _id, password, confirmPassword }, { rejectWithValue }) => {
    try {
      const response = await callAPIWithoutAuth(
        apiUrls.forgetPassword,
        {},
        "POST",
        { _id, password, confirmPassword },
      );

      if (response?.data?.success) {
        return response?.data?.data;
      } else {
        return rejectWithValue(
          response?.data?.message || "Failed to reset password",
        );
      }
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to reset password",
      );
    }
  },
);

export const uploadFile = createAsyncThunk(
  "auth/uploadFile",
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("tempImage", file);

      const response = await API(apiUrls.singleImage, {}, "POST", formData);

      if (response?.data?.status) {
        return response.data.data;
      } else {
        return rejectWithValue(response?.data?.message || "File upload failed");
      }
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        "File upload failed",
      );
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: JSON.parse(localStorage.getItem("userData")) || null,
    isAuthenticated: !!localStorage.getItem("accessToken"),
    loading: false,
    error: null,
    otpLoading: false,
    otpError: null,
    otpVerified: false,
    registeredUserId: null,
    uploadingFile: false,
    fileUploadError: null,
    uploadedFileUrl: null,
    forgotPassword: {
      loading: false,
      error: null,
      success: false,
      userId: null,
      email: null,
    },
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.otpError = null;
      state.fileUploadError = null;
    },
    clearForgotPasswordState: (state) => {
      state.forgotPassword = {
        loading: false,
        error: null,
        success: false,
        userId: null,
        email: null,
      };
      state.otpVerified = false;
      state.registeredUserId = null;
    },
    updateAuthUser: (state, action) => {
      const updatedData = action.payload;
      state.user = { ...state.user, ...updatedData };
      localStorage.setItem('userData', JSON.stringify(state.user));
    },
    setAuthUser: (state, action) => {
      state.user = action.payload;
      localStorage.setItem('userData', JSON.stringify(action.payload));
    },
    clearOtpState: (state) => {
      state.otpLoading = false;
      state.otpError = null;
      state.otpVerified = false;
      state.registeredUserId = null;
    },
    clearFileUploadState: (state) => {
      state.uploadingFile = false;
      state.fileUploadError = null;
      state.uploadedFileUrl = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(sendOtp.pending, (state) => {
        state.otpLoading = true;
        state.otpError = null;
      })
      .addCase(sendOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.registeredUserId = action.payload;
        state.forgotPassword.userId = action.payload;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.otpError = action.payload;
        state.error = action.payload;
      })
      .addCase(verifyOtp.pending, (state) => {
        state.otpLoading = true;
        state.otpError = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.otpVerified = true;
        state.isAuthenticated = true;
        state.user = action.payload.user;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.otpError = action.payload;
        state.error = action.payload;
      })
      .addCase(verifySignup.pending, (state) => {
        state.otpLoading = true;
        state.otpError = null;
      })
      .addCase(verifySignup.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.otpVerified = true;
        state.isAuthenticated = true;
        state.user = action.payload.user;
      })
      .addCase(verifySignup.rejected, (state, action) => {
        state.otpLoading = false;
        state.otpError = action.payload;
        state.error = action.payload;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
      })
      .addCase(forgotPassword.pending, (state) => {
        state.forgotPassword.loading = true;
        state.forgotPassword.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.forgotPassword.loading = false;
        state.forgotPassword.success = true;
        state.forgotPassword.userId = action.payload?._id;
        state.forgotPassword.email = action.payload?.email;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotPassword.loading = false;
        state.forgotPassword.error = action.payload;
      })
      .addCase(resetPassword.pending, (state) => {
        state.forgotPassword.loading = true;
        state.forgotPassword.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.forgotPassword.loading = false;
        state.forgotPassword.success = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.forgotPassword.loading = false;
        state.forgotPassword.error = action.payload;
      })
      .addCase(uploadFile.pending, (state) => {
        state.uploadingFile = true;
        state.fileUploadError = null;
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.uploadingFile = false;
        state.uploadedFileUrl = action.payload;
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.uploadingFile = false;
        state.fileUploadError = action.payload;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  clearForgotPasswordState,
  clearOtpState,
  updateAuthUser,
  setAuthUser,
  clearFileUploadState,
} = authSlice.actions;

export default authSlice.reducer;
